import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import PrivacySection from './components/PrivacySection';
import ContactPrivacy from './components/ContactPrivacy';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Cynizee</title>
        <meta name="description" content="Learn how Cynizee collects, uses, stores, and shares information when you use the Cynizee mobile app and related services." />
        <meta name="keywords" content="privacy policy, family privacy, child protection, data security, Cynizee" />
        <meta property="og:title" content="Privacy Policy - Cynizee" />
        <meta property="og:description" content="Cynizee respects your privacy. Read our full privacy policy to understand how we handle your family's information." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - Cynizee" />
        <meta name="twitter:description" content="Cynizee respects your privacy. Read our full privacy policy." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">

            {/* Hero */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl lg:text-5xl font-heading font-black text-foreground mb-4">Privacy Policy</h1>
              <p className="text-text-secondary text-lg">Effective date: April 10, 2026 &nbsp;·&nbsp; Last updated: April 10, 2026</p>
              <p className="mt-4 text-text-secondary max-w-2xl mx-auto">
                Cynizee ("we," "our," or "us") respects your privacy. This Privacy Policy explains how Cynizee collects, uses, stores, and shares information when you use the Cynizee mobile app and related services.
              </p>
            </div>

            <div className="space-y-6 mb-12">

              <PrivacySection title="1. Information We Collect" icon="Database" defaultExpanded={true}>
                <div className="space-y-5">
                  <p className="text-text-secondary">We may collect the following information:</p>

                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">Account Information</h4>
                    <ul className="space-y-1 text-text-secondary text-sm">
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Parent name</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Email address</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Authentication information used to sign in</span></li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">Family and Child Information</h4>
                    <ul className="space-y-1 text-text-secondary text-sm">
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Family code</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Child profile information such as child name, PIN, and settings</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Parent and child role information</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Chore assignments, approvals, rejections, and history</span></li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">Content You Provide</h4>
                    <ul className="space-y-1 text-text-secondary text-sm">
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Profile photos</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Chore proof photos</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Notes, comments, and feedback entered in the app</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Shopping list items, grocery requests, and related photos</span></li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">Device and App Information</h4>
                    <ul className="space-y-1 text-text-secondary text-sm">
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Device identifiers used to associate a child device with a child profile</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Push notification tokens</span></li>
                      <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>App activity needed to support app locking, approvals, and notifications</span></li>
                    </ul>
                  </div>
                </div>
              </PrivacySection>

              <PrivacySection title="2. How We Use Information" icon="Target">
                <div className="space-y-2">
                  <p className="text-text-secondary mb-3">We use information to:</p>
                  <ul className="space-y-2 text-text-secondary text-sm">
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Create and manage family accounts</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Allow parents and children to use Cynizee features</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Assign, track, approve, reject, and review chores</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Support app lock features and child device setup</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Send notifications, reminders, and approvals</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Store and display photos you upload</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Improve app reliability, performance, and security</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Respond to support requests</span></li>
                  </ul>
                </div>
              </PrivacySection>

              <PrivacySection title="3. How Information Is Stored" icon="Lock">
                <p className="text-text-secondary">
                  Information may be stored securely using third-party service providers that help operate Cynizee, including cloud database, authentication, storage, and notification services.
                </p>
              </PrivacySection>

              <PrivacySection title="4. Sharing of Information" icon="Share2">
                <div className="space-y-4">
                  <div className="bg-success/5 rounded-lg p-4">
                    <p className="font-heading font-semibold text-foreground mb-2">We do not sell personal information.</p>
                    <p className="text-text-secondary text-sm">We may share information only in these situations:</p>
                  </div>
                  <ul className="space-y-2 text-text-secondary text-sm">
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>With service providers that help us run the app</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>When required by law</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>To protect the rights, safety, and security of users, Cynizee, or others</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>In connection with a business transfer such as a merger, sale, or acquisition</span></li>
                  </ul>
                </div>
              </PrivacySection>

              <PrivacySection title="5. Children's Privacy" icon="Baby">
                <div className="space-y-3">
                  <div className="bg-accent/5 rounded-lg p-4 border-l-4 border-accent">
                    <p className="text-text-secondary text-sm">
                      Cynizee is designed for family use under parent supervision. Child information is managed by a parent or guardian through the app. Parents are responsible for the information they create, manage, or upload for child profiles.
                    </p>
                  </div>
                </div>
              </PrivacySection>

              <PrivacySection title="6. Photos and Uploaded Content" icon="Camera">
                <p className="text-text-secondary">
                  If you upload profile photos, chore proof photos, or shopping-related photos, those images may be stored and displayed within the app for your family's use.
                </p>
              </PrivacySection>

              <PrivacySection title="7. Notifications" icon="Bell">
                <p className="text-text-secondary">
                  Cynizee may use push notifications to send reminders, chore updates, approvals, rejections, and related alerts. You can manage notification permissions in your device settings.
                </p>
              </PrivacySection>

              <PrivacySection title="8. Data Retention" icon="Clock">
                <p className="text-text-secondary">
                  We retain information for as long as needed to provide the app and operate family accounts, unless a longer retention period is required by law. We may delete or anonymize information when it is no longer needed.
                </p>
              </PrivacySection>

              <PrivacySection title="9. Your Choices" icon="Settings">
                <div className="space-y-2">
                  <p className="text-text-secondary mb-3">You may be able to:</p>
                  <ul className="space-y-2 text-text-secondary text-sm">
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Update account information in the app</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Remove photos or content you uploaded</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Delete child profiles or family-related content</span></li>
                    <li className="flex items-start space-x-2"><div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></div><span>Contact us to request account deletion or data removal</span></li>
                  </ul>
                </div>
              </PrivacySection>

              <PrivacySection title="10. Security" icon="Shield">
                <p className="text-text-secondary">
                  We use reasonable administrative, technical, and organizational safeguards to protect information. No system is completely secure, and we cannot guarantee absolute security.
                </p>
              </PrivacySection>

              <PrivacySection title="11. Third-Party Services" icon="Globe">
                <p className="text-text-secondary">
                  Cynizee may rely on third-party services such as authentication, cloud storage, databases, and notifications. Their handling of data is governed by their own privacy policies.
                </p>
              </PrivacySection>

              <PrivacySection title="12. Changes to This Privacy Policy" icon="FileText">
                <p className="text-text-secondary">
                  We may update this Privacy Policy from time to time. If we make material changes, we will update the "Last updated" date and may provide additional notice where appropriate.
                </p>
              </PrivacySection>

              <PrivacySection title="13. Contact Us" icon="Mail">
                <div className="space-y-3">
                  <p className="text-text-secondary">
                    If you have questions about this Privacy Policy or want to request deletion of your data, contact us at:
                  </p>
                  <div className="bg-surface rounded-lg p-4 space-y-1 text-sm text-text-secondary">
                    <p className="font-heading font-semibold text-foreground">Cynizee</p>
                    <p>Email: <a href="mailto:support@cynizee.com" className="text-accent hover:underline">support@cynizee.com</a></p>
                    <p>Website: <a href="https://cynizee.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Cynizee.com</a></p>
                  </div>
                </div>
              </PrivacySection>

            </div>

            <ContactPrivacy />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-8">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-sm opacity-80">
              © {new Date()?.getFullYear()} Cynizee. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicy;